import axiosInstance from "utils/request";
import { z } from "zod";
import { parsed } from "./api";

const LidarClearanceLabelAxisValueSchema = z.object({
  axis_id: z.number(),
  axis_value: z.number(),
});
type LidarClearanceLabelAxisValue = z.infer<
  typeof LidarClearanceLabelAxisValueSchema
>;

export const LidarClearanceLabelSchema = z.object({
  region: z.string(),
  id: z.number(),
  system_id: z.number(),
  label: z.number(),
  label_name: z.string().nullable(),
  axis_values: z.array(LidarClearanceLabelAxisValueSchema),
});
export type LidarClearanceLabel = z.infer<typeof LidarClearanceLabelSchema>;

function labelsUrl(region: string, systemId: number): string {
  return `/lidar_clearance/systems/${region}/${systemId}/labels`;
}

export function fetchLabels(
  region: string,
  systemId: number
): Promise<LidarClearanceLabel[]> {
  return parsed(
    z.array(LidarClearanceLabelSchema),
    axiosInstance.get(labelsUrl(region, systemId))
  );
}

export function createLabel(
  region: string,
  systemId: number,
  label: number,
  labelName: string | null,
  axisValues: LidarClearanceLabelAxisValue[]
): Promise<LidarClearanceLabel> {
  return parsed(
    LidarClearanceLabelSchema,
    axiosInstance.post(labelsUrl(region, systemId), {
      label,
      label_name: labelName,
      axis_values: axisValues,
    })
  );
}

export function updateLabel(
  region: string,
  systemId: number,
  labelId: number,
  updates: {
    label?: number;
    label_name?: string | null;
    axis_values?: LidarClearanceLabelAxisValue[];
  }
): Promise<LidarClearanceLabel> {
  return parsed(
    LidarClearanceLabelSchema,
    axiosInstance.patch(`${labelsUrl(region, systemId)}/${labelId}`, updates)
  );
}

export async function deleteLabel(
  region: string,
  systemId: number,
  labelId: number
): Promise<void> {
  await axiosInstance.delete(`${labelsUrl(region, systemId)}/${labelId}`);
}

export function fillLabels(
  region: string,
  systemId: number,
  axisIds: number[]
): Promise<LidarClearanceLabel[]> {
  return parsed(
    z.array(LidarClearanceLabelSchema),
    axiosInstance.post(`${labelsUrl(region, systemId)}/fill`, {
      axis_ids: axisIds,
    })
  );
}

export function copyAxisLabels(
  region: string,
  systemId: number,
  axisId: number,
  sourceSystemId: number
): Promise<LidarClearanceLabel[]> {
  return parsed(
    z.array(LidarClearanceLabelSchema),
    axiosInstance.post(`${labelsUrl(region, systemId)}/copy`, {
      axis_id: axisId,
      source_system_id: sourceSystemId,
    })
  );
}
